import { kpiLabel } from './waterBalance';

/**
 * The Water Balance, shaped so it can be narrated.
 *
 * The panel does not draw a series. It draws four totals for one day and a
 * verdict on whether they add up, so handing over the response that produced
 * them would have the narrative describe a KPI-by-day series nobody is looking
 * at, and say nothing about the only question the panel asks.
 */

/** Keys this panel's insights, and names it in the summary across the page. */
export const BALANCE_PANEL_ID = 'water-balance';

/**
 * The components, each source withdrawn then consumed — the order the panel
 * draws them in, so a narrative that walks the rows walks the cards the reader
 * is looking at.
 *
 * What the site sent for treatment is not among them. The panel stopped drawing
 * it when it moved to the treatment cards below, and those read their own
 * latest reporting day rather than the balance's day, so a narrative quoting it
 * here would put a figure beside the card that contradicts it.
 *
 * Treated water stays, without a card of its own on this panel, because the
 * verdict turns on it: a site treating more than it withdrew does not balance,
 * and the panel says so in words above these figures.
 */
const COMPONENTS = [
  { name: 'Borewell withdrawn', of: (balance) => balance.groundWithdrawn },
  { name: 'Borewell consumed', of: (balance) => balance.groundConsumed },
  { name: 'Tanker withdrawn', of: (balance) => balance.tankerWithdrawn },
  { name: 'Tanker consumed', of: (balance) => balance.tankerConsumed },
  { name: 'Treated', of: (balance) => balance.treated },
];

/**
 * The four figures as widget rows, so the shared analysis can read them.
 *
 * They are grouped by name rather than by period because the balance is one
 * day: what is worth comparing is the components against each other, not the
 * day against itself.
 *
 * @param {object|null} balance - Output of `waterBalance`.
 * @returns {Array<object>} One row per component, empty when nothing balanced.
 */
export const balanceRows = (balance) => {
  if (!balance) return [];

  return COMPONENTS.map((component) => ({
    period_ts: balance.periodTs,
    displayPeriods: component.name,
    kpi: component.name,
    sum_value: component.of(balance),
  }));
};

/**
 * States what the panel concluded, for the narrative to work from.
 *
 * The verdicts are the point of the panel and none of them can be recovered
 * from four totals: that consumption exceeded its own source, or that more
 * water was treated than was ever withdrawn, is a judgement the page has
 * already made and the reader has already seen.
 *
 * @param {object|null} balance - Output of `waterBalance`.
 * @returns {string} A sentence, or an empty string when nothing balanced.
 */
export const describeBalance = (balance) => {
  if (!balance) return '';

  const parts = [
    `Water balance for one day on which every component reported. The site ${
      balance.balances ? 'balances' : 'does not balance'
    }.`,
  ];

  if (balance.consumedExceedsSource) {
    parts.push(
      'Groundwater consumption exceeds groundwater withdrawal, which cannot be physically true: water cannot leave a site it never entered, so a meter in that chain is misreading or treatment is drawing on stored volume.'
    );
  }

  if (balance.tankerConsumedIsAssumed) {
    parts.push(
      'Tanker consumption is not metered; what was delivered is taken as what was used, so treat it as an assumption rather than a reading.'
    );
  }

  if (balance.treatedExceedsWithdrawn) {
    parts.push(
      'More water was treated than was withdrawn from any source, which cannot be physically true either.'
    );
  }

  if (balance.silent?.length) {
    parts.push(
      `These inlets reported nothing: ${balance.silent.map(kpiLabel).join(', ')}.`
    );
  }

  return parts.join(' ');
};
